import React, { FC, useCallback } from 'react';
import translate from '@translate';
import { useLocation, useNavigate } from 'react-router-dom';
import BodyTopBlock, { ActionType } from 'src/components/BodyTopBlock';
import ExportTableAction from 'src/components/ExportTableAction';
import { ExportTypeType, SelectRowType } from 'src/types/table';
import StoreIcon from '@mui/icons-material/Store';
import ArchiveIcon from '@mui/icons-material/Archive';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CommuteIcon from '@mui/icons-material/Commute';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import PrintDisabledIcon from '@mui/icons-material/PrintDisabled';
import TrainIcon from '@mui/icons-material/Train';
import FlipIcon from '@mui/icons-material/Flip';
import DifferenceIcon from '@mui/icons-material/Difference';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import { AddIcon } from 'src/components/SkladonIcon/Icons';
import { OrderType } from 'src/types/order';
import {
    canEditAllowExpedition,
    canEditPrintFiles,
    canReorderShipping,
    getAllowedStatuses,
    getTabs
} from './functions';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import filterUnique from 'src/utils/filterUnique';
import { ImportIcon } from 'src/components/SkladonIcon/SmallIcons';
import { checkPermission, useCheckPermission } from '../../utils/checkPermission';
import { SOURCE_SYS_MYSKLADON } from 'skladon-types';
import { ExportDataType } from './index';

const MAXT_COUNT_EXPORT = 17000;

type TopBlockProps = {
    handleExport: (type: ExportTypeType, dataType: ExportDataType) => void;
    allProviders: boolean;
    archive: boolean;
    permissionArchive: boolean;
    setArchive: (value: boolean) => void;
    permissionOrderUpdate: boolean;
    permissionOrderDetail: boolean;
    permissionAllProviders: boolean;
    permissionOrderCreate: boolean;
    permissionShippingCreate: boolean;
    permissionDCIXImport: boolean;
    setAllProviders: (value: boolean) => void;
    selectedRows: SelectRowType[];
    ordersDataData?: OrderType[];
    orderStatusCodes: ConstantsCodeType[];
    orderStatusCodesObject: ConstantsCodesObjectType;
    reloadTableData: () => void;
    handleDowlonadCn22: () => void;
    handleReorderShipping: () => void;
    handleSynchronizeDCIX: () => void;
    handleMassPrintFiles: () => void;
    handleMassAllowExpeditions: () => void;
    handleAddRemoveProducts: () => void;
    handleMassChangeStatus: (statuses: ConstantsCodeType[]) => void;
    totalCount?: number;
    /** When true, bulk add/remove products is allowed for all orders (e.g. synced from e-shop). When false, only orders created in MySkladon (SOURCE_SYS myskladon). */
    providerAllowedOrderProductsEdit: boolean;
};

const TopBlock: FC<TopBlockProps> = (props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const selected = location.pathname.split('/')[2];
    const permissionExportOrders = useCheckPermission('ORDER.EXPORT.TABLE');
    const permissionExportOrdersProducts = useCheckPermission('ORDER.EXPORT.PRODUCTS');
    const permissionExportOrdersProductsSummarized = useCheckPermission('ORDER.EXPORT.PRODUCTS_SUMMARIZED');

    const handleChange = useCallback((newUrl: string) => {
        navigate('/orders/' + newUrl);
    }, []);

    const handleNew = useCallback(() => {
        navigate(`/orders/new/`);
    }, []);

    const handleImport = useCallback(() => {
        navigate(`/orders/import/`);
    }, []);

    const getEnableMultipleActions = (): {
        ableReorderShipping: boolean;
        ableAllowExpedition: boolean;
        ableEditPrintFiles: boolean;
        ableDowlonadCn22: boolean;
        ableEditStatuses: boolean;
        ableAddRemoveProduct: boolean;
        allowedStatuses: ConstantsCodeType[];
    } | null => {
        if (!props.selectedRows.length || !props.ordersDataData) {
            return null;
        }

        let ableReorderShipping = props.permissionShippingCreate;
        let ableAllowExpedition = props.permissionOrderUpdate;
        let ableEditPrintFiles = props.permissionOrderUpdate;
        let ableEditStatuses = props.permissionOrderUpdate;
        let ableDowlonadCn22 = props.permissionOrderDetail;

        const selectedStatuses: string[] = [];

        props.ordersDataData
            .filter((item) => props.selectedRows.includes(String(item.ID)))
            .forEach((item) => {
                if (ableReorderShipping && !canReorderShipping(item)) {
                    ableReorderShipping = false;
                }

                if (ableAllowExpedition && !canEditAllowExpedition(item, props.orderStatusCodesObject)) {
                    ableAllowExpedition = false;
                }

                if (ableEditPrintFiles && !canEditPrintFiles(item, props.orderStatusCodesObject)) {
                    ableEditPrintFiles = false;
                }

                if (item.STATUS) {
                    selectedStatuses.push(item.STATUS);
                }
            });

        let allowedStatuses: ConstantsCodeType[] = [];
        if (ableEditStatuses) {
            selectedStatuses.filter(filterUnique).forEach((status, index) => {
                if (index === 0) {
                    allowedStatuses = getAllowedStatuses(
                        { STATUS: status },
                        props.orderStatusCodes,
                        props.orderStatusCodesObject
                    );
                } else {
                    const thisStatusStatuses = getAllowedStatuses(
                        { STATUS: status },
                        props.orderStatusCodes,
                        props.orderStatusCodesObject
                    ).map((status) => status.value);

                    allowedStatuses = allowedStatuses.filter((status) => thisStatusStatuses.includes(status.value));
                }
            });
        }
        let ableAddRemoveProduct = false;
        if (props.permissionOrderUpdate) {
            const selectedOrders = props.ordersDataData.filter((item) => props.selectedRows.includes(String(item.ID)));
            const statusesOk = selectedStatuses
                .filter(filterUnique)
                .every((status) => checkPermission(`ORDER.STATUS.EDIT.${status.toUpperCase()}`));
            if (props.providerAllowedOrderProductsEdit) {
                ableAddRemoveProduct = statusesOk;
            } else {
                const allFromMyS = selectedOrders.every((item) => item.SOURCE_SYS === SOURCE_SYS_MYSKLADON);
                ableAddRemoveProduct = allFromMyS && statusesOk;
                console.log('allFromMyS', allFromMyS);
            }
            console.log('ableAddRemoveProduct', ableAddRemoveProduct);
            console.log('statusesOk', statusesOk);
            console.log('selectedOrders', selectedOrders);
            console.log('props.providerAllowedOrderProductsEdit', props.providerAllowedOrderProductsEdit);
        }
        return {
            ableReorderShipping,
            ableAllowExpedition,
            ableEditPrintFiles,
            ableEditStatuses,
            allowedStatuses,
            ableDowlonadCn22,
            ableAddRemoveProduct
        };
    };

    const ableMultipleActions = getEnableMultipleActions();

    const actions: ActionType[] = [
        {
            onClick: handleImport,
            icon: <ImportIcon />,
            tooltip: translate('orders.action.importOrders'),
            variant: 'outlined',
            hidden: !props.permissionOrderCreate || !props.permissionOrderUpdate || props.allProviders || props.archive
        },
        {
            node: (
                <ExportTableAction
                    title={translate(selected !== 'cod' ? 'orders.action.exportFile' : 'orders.action.exportFileCOD')}
                    handleExport={(type) => props.handleExport(type, 'orders')}
                />
            ),
            tooltip: translate(selected !== 'cod' ? 'orders.action.exportFile' : 'orders.action.exportFileCOD'),
            hidden: props.archive || !permissionExportOrders || (props.allProviders && (props.totalCount || 0) > MAXT_COUNT_EXPORT)
        },
        {
            onClick: () => props.setAllProviders(!props.allProviders),
            icon: <StoreIcon />,
            tooltip: translate('orders.allProviders'),
            variant: 'outlined',
            active: props.allProviders,
            hidden: !props.permissionAllProviders,
            disabled: props.archive
        },
        {
            onClick: () => props.setArchive(!props.archive),
            icon: <ArchiveIcon />,
            label: translate('archive.toggle'),
            tooltip: translate('archive.tooltip'),
            variant: 'outlined',
            active: props.archive,
            hidden: !props.permissionArchive,
            'data-testid': 'archive-toggle'
        },
        {
            onClick: () => props.reloadTableData(),
            icon: <AutorenewIcon />,
            tooltip: translate('orders.action.synchronizeTable'),
            variant: 'outlined'
        },

        {
            hidden: props.archive,
            items: [
                {
                    onClick: () => props.handleDowlonadCn22(),
                    icon: <PictureAsPdfIcon />,
                    label: translate('orders.action.dowlonadCn22'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.ableDowlonadCn22
                },
                {
                    onClick: () => props.handleReorderShipping(),
                    icon: <CommuteIcon />,
                    label: translate('orders.action.reorderShipping'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.ableReorderShipping
                },
                {
                    onClick: () => props.handleSynchronizeDCIX(),
                    icon: <SyncAltIcon />,
                    label: translate('orders.action.synchronizeDcix'),
                    variant: 'text',
                    hidden: !props.selectedRows.length || !props.permissionDCIXImport
                },
                {
                    onClick: () => props.handleMassPrintFiles(),
                    icon: <PrintDisabledIcon />,
                    label: translate('orders.action.printFiles'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.ableEditPrintFiles
                },
                {
                    onClick: () => props.handleMassAllowExpeditions(),
                    icon: <TrainIcon />,
                    label: translate('orders.action.allowExpeditions'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.ableAllowExpedition
                },
                {
                    onClick: () =>
                        props.handleAddRemoveProducts(),
                    icon: <DifferenceIcon />,
                    label: translate('orders.action.addRemoveProducts'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.ableAddRemoveProduct
                },
                {
                    onClick: () =>
                        ableMultipleActions?.allowedStatuses &&
                        props.handleMassChangeStatus(ableMultipleActions?.allowedStatuses),
                    icon: <FlipIcon />,
                    label: translate('orders.action.changeStatuses'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.allowedStatuses.length
                },
                { divider: true },

                {
                    node: (
                        <ExportTableAction
                            title={translate('orders.action.exportWithProducts')}
                            handleExport={(type) => props.handleExport(type, 'ordersProducts')}
                            icon={<AssignmentReturnedIcon />}
                            textWithIcon
                        />
                    ),
                    hidden: props.archive || props.allProviders || !permissionExportOrdersProducts,
                },
                {
                    node: (
                        <ExportTableAction
                            title={translate('orders.action.exportWithSummarizedProductsFileName')}
                            handleExport={(type) => props.handleExport(type, 'ordersProductsSummarized')}
                            icon={<AssignmentReturnedIcon />}
                            textWithIcon
                        />
                    ),
                    hidden: props.archive || props.allProviders || !permissionExportOrdersProductsSummarized,
                }
            ]
        },
        {
            label: translate('orders.action.createNew'),
            onClick: handleNew,
            icon: <AddIcon />,
            hidden: props.archive || props.allProviders || !props.permissionOrderCreate
        }
    ];

    return <BodyTopBlock tabs={getTabs()} selectTab={selected} onChangeTab={handleChange} actions={actions} />;
};

export default TopBlock;
